import uuid from '@vend/vend-uuid'
import angular from 'angular'
import VendNumber, { vn, round } from 'vend-number'
import { ApiCustomer, ApiStoreCredit } from '@vend/types'
import { PAYMENT_TYPES } from '@vend/business-components'

import { trackError } from '@vend/utilities'
import {
  recordStoreCredit,
  getCustomerStoreCredit,
} from '../components/store-credit/store-credit-resource/store-credit-resource'
import { CustomerBalances, Sale } from '../models/sale'
import {
  ReduxCurrentSale,
  selectCurrentSale,
} from '../state/slices/currentSale/currentSaleSlice'
import {
  customerManager,
  retailerSettingsManager,
  userManager,
  webRegisterStatusManager,
} from '../ng-compat/managers'
import { store } from '../state/store'

export interface StoreCreditBalance extends Partial<ApiStoreCredit> {
  balance: number
}

/**
 * Manages customer balances
 *
 * @module webregister.managers.customerBalancesManager
 */

export class CustomerBalancesManager {
  // ============================== Public API ============================== //

  /**
   * Retrieves the latest customer balances
   *
   * @todo  Ideally we should not be calling this when in offline mode, this does require us to change the display
   * of loyalty and on account totals.  Currently these display in offline mode and could potentially be out of date.
   * After we hide those in offline mode we can make this method dependant on being online
   *
   * @method getCustomerBalances
   *
   * @param  {Customer} customer
   *         The customer which is to be evaluated
   *
   * @return {Promise} A promise resolved with the given customer's balances
   */
  public async getCustomerBalances(
    customer: ApiCustomer
  ): Promise<CustomerBalances> {
    const [refreshedCustomer, storeCredit] = await Promise.all([
      customerManager.refreshCustomer(customer),
      this.getCustomerStoreCredit(customer) ?? { balance: 0 },
    ])

    return {
      storeCredit: storeCredit as ApiStoreCredit,
      loyalty: refreshedCustomer.loyalty_balance ?? 0,
      account: refreshedCustomer.balance ?? 0,
      onAccountLimit: refreshedCustomer.on_account_limit ?? 0,
      availableOnAccountLimitBalance: this.calculateOnAccountLimitBalance(
        refreshedCustomer as Pick<ApiCustomer, 'balance' | 'on_account_limit'>
      ),
    }
  }

  /**
   * Calculate the store credit balance for a sale.
   *
   * @method calculateStoreCreditBalance
   *
   * @param {Sale} sale
   *        The sale to calculate the current store credit balance on
   *
   * @return {Number} Return a value representing the current store credit balance for the customer
   */
  public calculateStoreCreditBalance() {
    const currentSale = selectCurrentSale(store.getState())
    if (
      !currentSale.customerBalances ||
      !currentSale.customerBalances.storeCredit
    ) {
      return 0
    }

    const currentStoreCreditBalance =
      currentSale.customerBalances.storeCredit.balance
    const totalStoreCreditPayments = this.getTotalPartialPayments(
      PAYMENT_TYPES.STORE_CREDIT,
      currentSale
    )
    const adjustedStoreCreditBalance = VendNumber.subtract(
      currentStoreCreditBalance,
      totalStoreCreditPayments
    )

    return adjustedStoreCreditBalance
  }

  /**
   * Calculate the customer's remaining on account limit available.
   *
   * @method calculateOnAccountLimitBalance
   *
   * @param {ApiCustomer} customer
   *        The customer's data
   *
   * @return {Number} Return a value representing the customer's on account balance and returns 0 if balance is negative or it is not set
   */
  public calculateOnAccountLimitBalance(
    customer: Pick<ApiCustomer, 'balance' | 'on_account_limit'>
  ) {
    const onAccountBalance = VendNumber.add(
      customer.on_account_limit || 0,
      customer.balance || 0
    )
    return onAccountBalance
  }

  /**
   * Calculates the total amount of partial payments made with the specified payment type. Excludes any 'retrieved'
   * payments unless the "includeRetrievedPayments" option is passed.
   *
   * @method getTotalPartialPayments
   *
   * @param {Object} options
   *
   *        @param {Sale} sale
   *               The Sale to check conditions against
   *
   *        @param {String} paymentType
   *               The payment type to use in the lookup for partial payments
   *
   *        @param {Boolean} [includeRetrievedPayments = false]
   *               Whether to include any retrieved payments (payments part of an API hyrdated sale)
   *
   * @return {Number} The total amount of partial payments made with the specified payment type
   */
  public getTotalPartialPayments(
    paymentType: number,
    sale: Readonly<Sale> | Readonly<ReduxCurrentSale>,
    includeRetrievedPayments: boolean = false
  ) {
    if (!sale) {
      return 0
    }
    return sale.payments.reduce((total, payment) => {
      if (
        payment.paymentType.type_id === paymentType &&
        (includeRetrievedPayments || !payment.retrieved)
      ) {
        return VendNumber.add(total, payment.amount)
      } else {
        return total
      }
    }, 0)
  }

  /**
   * Issues a store credit to the provided customer
   *
   * @method issueStoreCredit
   *
   * @param  {String} customerId
   *         The customer to receive the store credit
   *
   * @param  {Object} storeCreditData
   *
   *        @param {Number} storeCreditData.amount
   *               The amount of store credit to issue
   *
   *        @param {String} storeCreditData.notes
   *               The accompanying note
   *
   * @return {Promise} Resolved once the store credit has been issued to the customer
   */
  public issueStoreCredit(
    customerId: string,
    storeCreditData: {
      amount: number
      notes: string
    }
  ) {
    const issueStoreCreditData = {
      type: 'ISSUE',
      amount: vn(round(storeCreditData.amount, 2)).toNumber(),
      user_id: userManager.getLoggedInUser().id,
      notes: storeCreditData.notes,
      client_id: uuid(),
    }

    return recordStoreCredit(customerId, issueStoreCreditData)
  }

  // ============================== Private API ============================== //

  /**
   * Retrieves the latest customer store credit details from the api
   *
   * @method getCustomerStoreCredit
   *
   * @param  {Customer} customer
   *         The customer which is to be evaluated
   *
   * @return {Promise} A promise resolved with the given customer store credit
   */
  public getCustomerStoreCredit(
    customer: ApiCustomer
  ): Promise<StoreCreditBalance | null> {
    if (
      !webRegisterStatusManager.isOnline() ||
      !retailerSettingsManager.isStoreCreditEnabled()
    ) {
      return Promise.resolve(null)
    }

    return getCustomerStoreCredit(customer.id)
      .then(response => {
        if (response.ok) {
          return response.json()
        }

        return Promise.reject(response)
      })
      .catch(error => {
        if (error.status === 404) {
          return {
            balance: 0,
          }
        }

        trackError(
          '[CustomerBalanceManager] Failed to retrieve customer store credit data',
          {
            data: { error },
          }
        )
        return null
      })
  }
}

export const customerBalancesManager = new CustomerBalancesManager()

angular
  .module('webregister.managers')
  .factory('customerBalancesManager', () => customerBalancesManager)
